import { Delete } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Divider, IconButton, Stack, Typography } from '@mui/material';
import { cloneDeep, get, set } from 'lodash';
import React, { FC, useEffect, useState } from 'react';
import ScrollToTop from 'src/components/layout/ScrollToTop';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import LoadingIconOnly from 'src/components/ui-component/loading/LoadingIconOnly';
import MUIAlert from 'src/components/ui-component/mui-alert/MUIAlert';
import MuiSnackbar from 'src/components/ui-component/mui-snackbar/MuiSnackbar';
import { useAuthentication } from 'src/context/AuthContext';
import GetRecord from 'src/lib/api/GetRecord';
import POPLEVEL from 'src/lib/api/Populations.json';
import { PostRecord } from 'src/lib/api/PostRecord';
import PutRecord from 'src/lib/api/PutRecord';
import { APIS } from 'src/lib/constants/apis';
import { AUTH_TOKEN_DATA, FORM_FIELDS_TYPES, NICHT_IN_HESSEN, NOT_FOUND_ERROR_MESSAGE } from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { getFieldNamefromDataPath, removeDataKeys, removeNullDeep, T } from 'src/utils/helper';
import OrganisationDataValidator from './OrganisationDataValidator';

// import schemas
import OrganisationSchema from 'src/lib/paths/organisation_FULL_Paths.json';
let P = OrganisationSchema.attributes;

const OrganisationData: FC<{}> = () => {
	const { authenticatedData } = useAuthentication();

	// form and componente States
	const [loading, setLoading] = useState<boolean>(true);
	const [updateSuccess, setUpdateSuccess] = useState<boolean | null>(null);
	const [wsError, setWsError] = useState<boolean>(false);

	// formvalues
	const [formDataChanged, setFormDataChanged] = useState(false);
	const [organisationData, setOrganisationData] = useState<Record<string, any>>({});

	// Error handling states
	const [formErrors, setFormErrors] = useState<Record<string, any>>({});
	const [postError, setPostError] = useState<string | null>(null);

	// handler for all input changes in the form
	const handleInputChange = (path: string, value: any) => {
		setFormDataChanged(true);
		setUpdateSuccess(null);
		setPostError(null);
		let organisationDataCopy = cloneDeep(organisationData);
		set(organisationDataCopy, path, value);
		setOrganisationData(organisationDataCopy);
	};

	// handlers for mailbenachrichtigungen
	// ADD OR DELETE
	const handleAddEmail = () => {
		let orgaCopy = cloneDeep(organisationData);
		set(orgaCopy, `${P.Mailbenachrichtungen_Mail.path}.${get(organisationData, P.Mailbenachrichtungen_Mail.path, []).length}`, {
			E_45_Mail_45_Adresse: '',
		});
		setOrganisationData(orgaCopy);
		setFormDataChanged(true);
	};
	const handleDeleteEmail = (index: number) => {
		let orgaCopy = cloneDeep(organisationData);
		const allEmailCopy = get(orgaCopy, P.Mailbenachrichtungen_Mail.path, []);
		set(
			orgaCopy,
			P.Mailbenachrichtungen_Mail.path,
			allEmailCopy.filter((_: any, i: number) => i !== index),
		);
		setOrganisationData(orgaCopy);
		setFormDataChanged(true);
	};

	// validation
	const validateOrganisationData = () => {
		let orgaCopy = cloneDeep(organisationData);

		let errorObject = {};
		let errs = OrganisationDataValidator(orgaCopy).validate(orgaCopy);
		for (let error of errs) {
			set(errorObject, error.path, get(error, 'message'));
		}
		setFormErrors(errorObject);
		return Object.keys(errorObject).length === 0;
	};

	// Submit Handler
	const handleUpdateOrganisationData = async () => {
		if (validateOrganisationData()) {
			try {
				setLoading(true);
				let res = await PutRecord(APIS.Organisation, get(authenticatedData, AUTH_TOKEN_DATA.organisationId), {
					data: get(organisationData, 'attributes'),
				});
				if (get(res, 'data.id')) {
					// Create Nachricht for address change
					let nachrichtData = {
						Antragsteller: get(authenticatedData, AUTH_TOKEN_DATA.organisationId),
						Allgemein_Betreff: `Die Organisationsdaten wurden aktualisiert.`,
						Allgemein_Gesendet: new Date(),
						Allgemein_Erstellt: new Date(),
						Allgemein_Ordner: 'Postausgang',
						Allgemein_Inhalt: `Die Organisation "${get(organisationData, P.Geschaeftsbezogene_Daten_Name_Der_Organisation.path, '')}" hat ihre Daten geändert.`,
					};
					const nachrichtRes = await PostRecord(APIS['Portal-Nachricht'], { data: nachrichtData });

					// Sending Pushnotification
					const pushNotificationData = [
						{ api: APIS.Organisation, id: get(res, 'data.id') },
						{ api: APIS['Portal-Nachricht'], id: get(nachrichtRes, 'data.id') },
					];
					await PostRecord(APIS.PushNotificaiton, { data: { entities: pushNotificationData } });
					setUpdateSuccess(true);
				}
			} catch (err: any) {
				const errorMessage = getErrorMessage(err);
				setPostError(errorMessage);
				console.error('ERROR: Updating Organisation Data ', err);
			} finally {
				setLoading(false);
				setFormDataChanged(false);
			}
		} else {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}
	};

	useEffect(() => {
		const fetchOrganisation = async () => {
			setLoading(true);
			try {
				let res = await GetRecord(APIS.Organisation, get(authenticatedData, AUTH_TOKEN_DATA.organisationId), POPLEVEL.organisation.FULL);
				setOrganisationData(removeNullDeep(removeDataKeys(get(res, 'data', {}))));
			} catch (err) {
				console.error('ERROR Fetching Organisation ', err);
				setWsError(true);
			} finally {
				setLoading(false);
			}
		};
		fetchOrganisation();
	}, [authenticatedData]);

	if (loading) return <LoadingIconOnly />;

	const allEmailsFields = get(organisationData, P.Mailbenachrichtungen_Mail.path, []);

	return (
		<>
			<MuiSnackbar showSnackbar={wsError} closeSnackbar={() => setWsError(false)} alertMessage={NOT_FOUND_ERROR_MESSAGE} error={wsError} />

			<Typography variant='h3' component='h2'>
				Organisationsprofil
			</Typography>

			{updateSuccess && (
				<>
					<ScrollToTop />
					<MUIAlert
						infoType={'success'}
						title='Ihre Organisationsdaten wurden erfolgreich aktualisiert.'
						message={
							'Bitte beachten Sie, dass diese Änderung nur Auswirkung auf zukünftige Anträge hat. Bereits bewilligte Anträge sind davon nicht betroffen. Anträge die noch nicht bewilligt wurden und noch in der Bearbeitung sind müssen angepasst werden. Sie finden dazu im Block Antragsteller einen Button um die aktuellen Daten zu übernehmen.'
						}
					/>
				</>
			)}
			{postError && (
				<>
					<ScrollToTop />
					<MUIAlert infoType='error' title='Ihre Organisationsdaten konnten nicht aktualisiert werden.' message={postError} />
				</>
			)}

			{/* Section 1: Organisation Data */}
			<CreateForm
				formStruc={formStrucOrganisationData()}
				formValues={organisationData}
				formErrors={formErrors}
				updateFormValuesFunc={handleInputChange}
				api={APIS.Organisation}
			/>

			<Divider sx={{ my: 4 }} />

			<Typography sx={{ fontWeight: '600', mb: 1 }}>Vertretungsberechtigte gemäß Handelsregister, Satzung oder Gesetz</Typography>
			<Typography>
				Bitte geben Sie hier <u>in der ersten Zeile</u> die vertretungsberechtigte Person an, die den Antrag auf Förderung für Ihr Projekt
				unterzeichnen wird.
			</Typography>
			<CreateForm
				formStruc={formStrucVertretungsberechtigte()}
				formValues={organisationData}
				formErrors={formErrors}
				updateFormValuesFunc={handleInputChange}
				api={APIS.Organisation}
				size='small'
			/>
			{/* <CreateForm
				formStruc={formStrucAnsprechpartner()}
				formValues={organisationData}
				formErrors={formErrors}
				updateFormValuesFunc={handleInputChange}
				api={APIS.Organisation}
				size='small'
			/> */}

			<Divider sx={{ my: 4 }} />

			{/* Section 3: Adressdaten */}
			<Typography variant='h4' component='h3' sx={{ mb: 2 }}>
				Adressdaten
			</Typography>
			<CreateForm
				formStruc={formStrucAdressDaten()}
				formValues={organisationData}
				formErrors={formErrors}
				updateFormValuesFunc={handleInputChange}
				api={APIS.Organisation}
			/>

			<Divider sx={{ my: 4 }} />

			{/* Section 4: Kontaktdaten */}
			<Typography variant='h4' component='h3' sx={{ mb: 2 }}>
				Kontaktdaten
			</Typography>
			<CreateForm
				formStruc={formStrucKontaktDaten()}
				formValues={organisationData}
				formErrors={formErrors}
				updateFormValuesFunc={handleInputChange}
				api={APIS.Organisation}
			/>

			<Divider sx={{ my: 4 }} />

			{/* Section 5: Mailbenachrichtigungen */}
			<Typography variant='h4' component='h3' sx={{ mb: 2 }}>
				Mailbenachrichtigungen
			</Typography>
			<Typography sx={{ mb: 2 }}>
				Über die hier angegebene E-Mail-Adresse informieren wir Sie über alle relevanten Neuigkeiten zu Ihrem Projekt.
			</Typography>
			<Typography>
				Optional können Sie weitere E-Mail-Adressen eintragen, an die diese Informationen zusätzlich versendet werden sollen.
			</Typography>
			<Stack direction={'row'} alignItems={'right'} justifyContent={'flex-end'} spacing={3} mt={3}>
				<Button onClick={handleAddEmail} size={'small'} endIcon={<AddIcon />}>
					Hinzufügen
				</Button>
			</Stack>

			{allEmailsFields.map((_: any, i: number) => {
				return (
					<Stack key={i} direction={'row'} alignContent={'center'} justifyContent={'flex-start'} alignItems={'self-end'}>
						<Box sx={{ width: '90%' }}>
							<CreateForm
								formStruc={mapFormStrucMailBenachrichtigung(formStrucMailBenachrichtigung(), i)}
								formValues={organisationData}
								formErrors={formErrors}
								updateFormValuesFunc={handleInputChange}
								api={APIS.Organisation}
							/>
						</Box>
						<IconButton onClick={() => handleDeleteEmail(i)} disabled={i === 0}>
							<Delete />
						</IconButton>
					</Stack>
				);
			})}

			<Divider sx={{ my: 4 }} />
			<Typography variant='h4' component={'h3'} sx={{ mb: 2 }}>
				Steuerinformationen
			</Typography>
			<CreateForm
				formStruc={formStrucSteuerInformationen(organisationData)}
				formValues={organisationData}
				formErrors={formErrors}
				updateFormValuesFunc={handleInputChange}
				api={APIS.Organisation}
			/>

			<Divider sx={{ my: 4 }} />
			<Typography variant='h4' component={'h3'} sx={{ mb: 2 }}>
				Vollständigkeit des Organisationsprofils
			</Typography>
			<Typography sx={{ mb: 2 }}>
				Die Daten aus dem Organisationsprofil werden automatisch in die Antragsformulare übertragen. Die Vollständigkeit der Angaben vereinfacht
				daher den Prozess der Antragstellung. Bitte bestätigen Sie hier, dass alle Informationen angegeben wurden - dann wird der Hinweis in “Mein
				Bereich” entfernt:
			</Typography>
			<CreateForm
				formStruc={formStrucProfilVollstaendig()}
				formValues={organisationData}
				formErrors={formErrors}
				updateFormValuesFunc={handleInputChange}
				api={APIS.Organisation}
			/>

			<Box sx={{ textAlign: 'right', mt: '2rem' }}>
				<Button disabled={!formDataChanged} onClick={handleUpdateOrganisationData}>
					Aktualisieren
				</Button>
			</Box>
		</>
	);
};
export default OrganisationData;

/**
 * ******************* ZUERST LESEN ********************
 * Erstmal alle auskommentierten Felder so auskommentiert lassen.
 * Auch im OrganisationDataValidator.
 * Es kann wieder sein, dass HA wieder die Felder haben möchte, dann müssen sie nur wieder einkommentiert werden. Es gibt aktuell keinen Grund die Felder komplett zu löschen, da sie ja vielleicht doch wieder gebraucht werden.
 * Das passiert sehr häufig in diesem Projekt, dass Anforderungen sich ändern und Dinge die einmal nicht gebraucht wurden, dann doch wieder gebraucht werden. Daher lieber auskommentieren als löschen.
 * ****************************************************
 */

// Form Structure for Organisation Data
const formStrucOrganisationData = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Geschaeftsbezogene_Daten_Name_Der_Organisation,
					tooltip:
						'Bitte geben Sie den vollständigen rechtsverbindlichen Namen Ihrer Organisation inklusive Rechtsform an (z. B. GmbH, UG (haftungsbeschränkt), AG, e. V.), wie im Handels- oder Vereinsregister eingetragen.',
				},
				P.Geschaeftsbezogene_Daten_Rechtsform_Der_Organisation,
			],
		},
		{
			fields: [
				P.Unternehmensprofil_Gruendungsdatum,
				{
					...P.Unternehmensprofil_Handelsregisternummer,
					label: 'Registernummer',
					tooltip: 'Handels-, Vereins-, Partnerschafts-, Genossenschaftsregister',
				},
			],
		},
		// { fields: [P.Unternehmensprofil_Name_Des_Geschaeftsfuehrers] },
		// {
		// 	fields: [
		// 		P.Unternehmensprofil_Oeffentlicher_Auftraggeber,
		// 		P.Unternehmensprofil_Sektorenauftraggeber,
		// 		P.Unternehmensprofil_Oe_PNV_Lizenz_Vorhanden,
		// 		P.Unternehmensprofil_Verpf_0050602990_fuehrung_Von_Ausschreibungen,
		// 	],
		// 	userProps: { direction: 'column', columnToDivide: 1 },
		// },
	];
	return struc;
};

// Form Structure for Vertretungsberechtigte
const formStrucVertretungsberechtigte = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Unternehmensprofil_Zeichnungsberechtigte,
					type: FORM_FIELDS_TYPES.componentTable,
					// tableLabel: 'Vertretungsberechtigte gemäß Handelsregister, Satzung oder Gesetz',
					fieldProps: {
						Telefonnummer: { maxWidth: '5.5rem' },
						Abteilung: { maxWidth: '3rem' },
					},
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};
// // Form Structure for Ansprechpartner
// const formStrucAnsprechpartner = () => {
// 	let struc: FormStrucItem[] = [
// 		{
// 			fields: [
// 				{
// 					...P.Unternehmensprofil_Ansprechpartnerin,
// 					type: FORM_FIELDS_TYPES.componentTable,
// 					tableLabel: 'Ansprechpartner/in',
// 				},
// 			],
// 			userProps: { direction: 'column', columnToDivide: 1 },
// 		},
// 	];
// 	return struc;
// };

// Form Structure for Adressdaten
const formStrucAdressDaten = () => {
	let struc: FormStrucItem[] = [
		{ fields: [P.Adressdaten_Strasse_Und_Hausnummer, P.Adressdaten_Adresszusatz] },
		{ fields: [P.Adressdaten_PLZ, P.Adressdaten_Ort] },
		{ fields: [{ ...P.Adressdaten_Landkreis_Kreisfreie_Stadt, sortAlphabetically: true, enumsLastPlace: [NICHT_IN_HESSEN] }] },
	];
	return struc;
};

// Form Structure for Kontaktdaten
const formStrucKontaktDaten = () => {
	let struc: FormStrucItem[] = [
		{ fields: [P.Kontaktdaten_E_Mail, P.Kontaktdaten_Fax] },
		{ fields: [P.Kontaktdaten_Telefon, P.Kontaktdaten_Webadresse] },
	];
	return struc;
};

// Form Structure for Mailbenachrichtigungen
const formStrucMailBenachrichtigung = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [{ ...P.Mailbenachrichtungen_Mail.schema.attributes.Mailbenachrichtungen_Mail, label: 'E-Mail-Adresse' }],
		},
	];
	return struc;
};

// Form Structure for Steuerinformationen
const formStrucSteuerInformationen = (_organisationData: any) => {
	let struc: FormStrucItem[] = [
		{ fields: [P.Unternehmensprofil_Steuernummer, P.Unternehmensprofil_Wirtschafts_Identifikationsnummer] },
		{ fields: [P.Unternehmensprofil_U_St_Id_Nr] },
		{
			fields: [
				{
					...P.Unternehmensprofil_Angaben_Zum_Vorsteuerabzug,
					type: FORM_FIELDS_TYPES.radio,
					label: 'Angaben zum Vorsteuerabzug',
					direction: 'column',
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};

const formStrucProfilVollstaendig = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [{ ...P.Organisationsprofil_Vollstaendig, label: 'Ich bestätige, dass mein Organisationsprofil vollständig und richtig ist.' }],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};

const mapFormStrucMailBenachrichtigung = (formStruc: any[], index: number) => {
	let result = formStruc.map((row) => {
		return {
			...row,
			fields: (get(row, 'fields', []) as Array<any>).map((value) => {
				let pathWithoutIndex = get(value, 'path', '') as string;
				return {
					...value,
					path:
						pathWithoutIndex.slice(0, P.Mailbenachrichtungen_Mail.path.length) +
						`.${index}` +
						pathWithoutIndex.slice(P.Mailbenachrichtungen_Mail.path.length),
				};
			}),
		};
	});
	return result;
};

const getErrorMessage = (err: any) => {
	let errorMessage = 'Es gab ein Problem beim Senden der Organisationsdaten. Bitte versuchen Sie es später erneut.';
	let errorResponse;
	let ustIdNr = getFieldNamefromDataPath(P.Unternehmensprofil_U_St_Id_Nr.path);
	try {
		// If err.message contains a JSON string, parse it, otherwise use old err
		errorResponse = typeof err.message === 'string' ? JSON.parse(err.message) : err;
		errorResponse = errorResponse?.error || errorResponse; // Ensure we extract the correct error structure
	} catch (parseError) {
		console.error('Error parsing the error response:', parseError);
		errorResponse = err; // Fall back to the original error
	}

	// Extract validation errors if available
	const strapiValidationErrors = errorResponse?.details?.errors;

	if (Array.isArray(strapiValidationErrors)) {
		const errorUnternehmen = strapiValidationErrors.find((error: any) => error.path?.[0] === ustIdNr);

		errorMessage = errorUnternehmen ? `Bitte geben Sie eine gültige ${T(ustIdNr)} ein.` : errorMessage;
	}

	return errorMessage;
};
